import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  queryClient,
  apiRequest,
  getAccessToken,
  setAccessToken,
} from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { messageServeur } from "@/lib/erreur-serveur";
import { setUser as sentrySetUser, clearUser as sentryClearUser } from "@/lib/sentry";

export type ParishMembership = {
  id: string;
  name: string;
  role: string;
  isPrimary: boolean;
};

export type AuthUser = {
  id: string;
  username: string;
  fullName: string;
  role: string;
  roles?: string[];
  parish: string;
  /** Multi-tenant V1.5 — paroisses du staff */
  parishes?: ParishMembership[];
  /** Paroisse primaire (null pour admin global) */
  primaryParishId?: string | null;
  /** Mot de passe provisoire (fiche de connexion) : à remplacer d'abord. */
  mustChangePassword?: boolean;
};

type LoginResponse = AuthUser & {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
};

type AuthContextType = {
  user: AuthUser | null;
  isLoading: boolean;
  loginMutation: ReturnType<typeof useLoginMutation>;
  registerMutation: ReturnType<typeof useRegisterMutation>;
  logoutMutation: ReturnType<typeof useLogoutMutation>;
};

function useLoginMutation() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: { username: string; password: string }) => {
      const res = await apiRequest("POST", "/api/login", data);
      return (await res.json()) as LoginResponse;
    },
    onSuccess: (data: LoginResponse) => {
      setAccessToken(data.accessToken);
      const { accessToken: _at, refreshToken: _rt, expiresIn: _exp, ...user } = data;
      queryClient.setQueryData(["/api/user"], user);
      // Identifie l'utilisateur dans Sentry (no-op si DSN absent)
      sentrySetUser({
        id: user.id,
        email: user.username,
        username: user.fullName,
        paroisseId: user.primaryParishId ?? user.parish,
        role: user.role,
      });
      toast({
        title: `Bienvenue, ${user.fullName}`,
        description: "Connexion réussie",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur de connexion",
        // Le message du service (« Identifiants invalides »…), jamais le
        // JSON brut de la réponse.
        description: messageServeur(error, {}, "Connexion impossible pour le moment. Réessayez dans un instant."),
        variant: "destructive",
      });
    },
  });
}

function useRegisterMutation() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: {
      username: string;
      password: string;
      fullName: string;
      role?: string;
      parish?: string;
    }) => {
      const res = await apiRequest("POST", "/api/register", data);
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: "Compte créé",
        description: "Vous pouvez maintenant vous connecter",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur d'inscription",
        description: messageServeur(error, {}, "Inscription impossible pour le moment. Réessayez dans un instant."),
        variant: "destructive",
      });
    },
  });
}

function useLogoutMutation() {
  return useMutation({
    mutationFn: async () => {
      try {
        await apiRequest("POST", "/api/logout");
      } catch {
        // best-effort, on nettoie meme en cas d'erreur reseau
      }
    },
    onSuccess: () => {
      setAccessToken(null);
      queryClient.setQueryData(["/api/user"], null);
      queryClient.clear();
      // Détache l'utilisateur de la session Sentry après logout
      sentryClearUser();
    },
  });
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/user"],
    retry: false,
    staleTime: Infinity,
    enabled: !!getAccessToken(),
    queryFn: async () => {
      const token = getAccessToken();
      if (!token) return null;
      const res = await fetch("/api/user", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setAccessToken(null);
        return null;
      }
      if (!res.ok) throw new Error("Erreur reseau");
      return res.json();
    },
  });

  const loginMutation = useLoginMutation();
  const registerMutation = useRegisterMutation();
  const logoutMutation = useLogoutMutation();

  // Synchronise l'utilisateur Sentry au montage / changement de session
  // (rehydration après reload, expiration de token, …)
  useEffect(() => {
    if (user) {
      sentrySetUser({
        id: user.id,
        email: user.username,
        username: user.fullName,
        paroisseId: user.primaryParishId ?? user.parish,
        role: user.role,
      });
    } else if (!isLoading) {
      sentryClearUser();
    }
  }, [user, isLoading]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        loginMutation,
        registerMutation,
        logoutMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
